import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X, Truck, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks,
  format, isSameMonth, isSameDay, isToday, parseISO, isWithinInterval,
} from "date-fns";
import { api, apiError } from "@/lib/api";

export const STATUS_DOT = {
  Enquiry: "#F59E0B",
  Confirmed: "#10B981",
  Active: "#3B82F6",
  Completed: "#64748B",
};

function buildEvents(clients) {
  const events = [];
  for (const c of clients) {
    if (c.hire_start) events.push({ client: c, date: c.hire_start, type: "Delivery" });
    if (c.hire_end) events.push({ client: c, date: c.hire_end, type: "Collection" });
  }
  return events;
}

function EventChip({ event, onClick }) {
  const { client, type } = event;
  const color = STATUS_DOT[client.status] || STATUS_DOT.Enquiry;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(event); }}
      className="w-full text-left flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] font-medium text-white truncate hover:opacity-85"
      style={{ backgroundColor: color }}
      data-testid={`booking-event-${client.id}-${type.toLowerCase()}`}
    >
      {type === "Delivery" ? <Truck className="h-3 w-3 shrink-0" /> : <PackageCheck className="h-3 w-3 shrink-0" />}
      <span className="truncate">{type} · {client.name}</span>
    </button>
  );
}

function DayJobsDialog({ day, events, actives, onClose, onSelectJob }) {
  const jobs = [
    ...events.map((ev) => ({ key: `${ev.client.id}-${ev.type}`, label: ev.type, client: ev.client, event: ev })),
    ...actives
      .filter((c) => !events.some((ev) => ev.client.id === c.id))
      .map((c) => ({ key: `${c.id}-active`, label: "On hire", client: c, event: { client: c, type: "On hire" } })),
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="day-jobs-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900" data-testid="day-jobs-title">{format(day, "EEEE d MMMM yyyy")}</h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid="day-jobs-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4">
          {jobs.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6" data-testid="day-jobs-empty">No bookings on this day.</p>
          ) : (
            <ul className="space-y-2">
              {jobs.map((job) => (
                <li key={job.key}>
                  <button
                    onClick={() => onSelectJob(job.event)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-slate-200 rounded-md hover:bg-slate-50 text-left transition-colors"
                    data-testid={`day-job-${job.client.id}-${job.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span>
                      <span className="block text-sm font-medium text-slate-900">{job.client.name}</span>
                      <span className="block text-xs text-slate-500">{job.client.address || "—"}</span>
                    </span>
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-full text-white shrink-0"
                      style={{ backgroundColor: STATUS_DOT[job.client.status] || STATUS_DOT.Enquiry }}
                    >
                      {job.label}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailDialog({ event, onClose }) {
  const { client: c, type } = event;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="booking-detail-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{type} — {c.name}</h2>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid="booking-detail-close"><X className="h-5 w-5" /></button>
        </div>
        <dl className="p-6 space-y-3 text-sm">
          {[
            ["Status", c.status],
            ["Phone", c.phone || "—"],
            ["Email", c.email || "—"],
            ["Address", c.address || "—"],
            ["Hire Start", c.hire_start || "—"],
            ["Hire End", c.hire_end || "—"],
            ["Notes", c.notes || "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex gap-3">
              <dt className="w-24 shrink-0 font-semibold text-slate-500">{k}</dt>
              <dd className="text-slate-800" data-testid={`booking-detail-${k.toLowerCase().replace(" ", "-")}`}>{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

export default function Bookings() {
  const [clients, setClients] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    api.get("/admin/clients").then((res) => setClients(res.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const events = useMemo(() => buildEvents(clients), [clients]);
  const eventsOn = (day) => events.filter((ev) => isSameDay(parseISO(ev.date), day));
  const activeOn = (day) =>
    clients.filter((c) => {
      if (!c.hire_start || !c.hire_end) return false;
      try {
        return isWithinInterval(day, { start: parseISO(c.hire_start), end: parseISO(c.hire_end) });
      } catch {
        return false;
      }
    });

  const days = useMemo(() => {
    if (view === "month") {
      const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
      const out = [];
      for (let d = start; d <= end; d = addDays(d, 1)) out.push(d);
      return out;
    }
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [cursor, view]);

  const nav = (dir) => setCursor((c) => (view === "month" ? addMonths(c, dir) : addWeeks(c, dir)));
  const title = view === "month" ? format(cursor, "MMMM yyyy") : `Week of ${format(days[0], "d MMM yyyy")}`;

  return (
    <div data-testid="booking-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Booking Calendar</h1>
          <p className="text-sm text-slate-500 mt-1">Deliveries, active hires and collections</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-slate-300 rounded-md overflow-hidden" data-testid="calendar-view-toggle">
            {["month", "week"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`view-${v}-button`}
                className={`px-4 py-2 text-sm font-medium capitalize ${view === v ? "bg-[#1A237E] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                {v}
              </button>
            ))}
          </div>
          <button onClick={() => nav(-1)} className="p-2 border border-slate-300 rounded-md bg-white hover:bg-slate-50" aria-label="Previous" data-testid="calendar-prev-button"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setCursor(new Date())} className="px-3 py-2 border border-slate-300 rounded-md bg-white text-sm font-medium hover:bg-slate-50" data-testid="calendar-today-button">Today</button>
          <button onClick={() => nav(1)} className="p-2 border border-slate-300 rounded-md bg-white hover:bg-slate-50" aria-label="Next" data-testid="calendar-next-button"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-slate-600" data-testid="calendar-legend">
        {Object.entries(STATUS_DOT).map(([s, color]) => (
          <span key={s} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} /> {s}
          </span>
        ))}
      </div>

      <h2 className="text-lg font-semibold text-slate-800 mb-3" data-testid="calendar-title">{title}</h2>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 text-center">{d}</div>
          ))}
        </div>
        <div className={`grid grid-cols-7 ${view === "month" ? "" : "divide-x divide-slate-100"}`}>
          {days.map((day) => {
            const dayEvents = eventsOn(day);
            const actives = activeOn(day);
            return (
              <div
                key={day.toISOString()}
                onClick={() => setSelectedDay(day)}
                className={`${view === "month" ? "min-h-[110px] border-b border-r border-slate-100" : "min-h-[300px]"} p-1.5 cursor-pointer hover:bg-slate-50 transition-colors ${
                  view === "month" && !isSameMonth(day, cursor) ? "bg-slate-50/60" : "bg-white"
                }`}
                data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-semibold inline-flex h-6 w-6 items-center justify-center rounded-full ${
                    isToday(day) ? "bg-[#1A237E] text-white" : isSameMonth(day, cursor) || view === "week" ? "text-slate-700" : "text-slate-400"
                  }`}>
                    {format(day, "d")}
                  </span>
                  {actives.length > 0 && (
                    <span className="flex -space-x-1" title={`Active hire: ${actives.map((a) => a.name).join(", ")}`}>
                      {actives.slice(0, 3).map((a) => (
                        <span key={a.id} className="h-2 w-2 rounded-full ring-1 ring-white" style={{ backgroundColor: STATUS_DOT[a.status] || STATUS_DOT.Active }} />
                      ))}
                    </span>
                  )}
                </div>
                <div className="mt-1 space-y-1">
                  {dayEvents.map((ev) => (
                    <EventChip key={`${ev.client.id}-${ev.type}`} event={ev} onClick={setSelected} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <DayJobsDialog
          day={selectedDay}
          events={eventsOn(selectedDay)}
          actives={activeOn(selectedDay)}
          onClose={() => setSelectedDay(null)}
          onSelectJob={(ev) => {
            setSelectedDay(null);
            setSelected(ev);
          }}
        />
      )}
      {selected && <DetailDialog event={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
